import React, { useState } from 'react';
import { Table, Card, Typography, Alert } from 'antd';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'src/components/design-system';
import { Select } from 'src/components/design-system/forms';
import { useUploadContextFactory } from './upload-contexts/use-upload-context-factory';
import { getUnmappedRequiredFields, suggestProperty } from './utils/column-mapping';
import styles from './column-mapper.module.scss';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';

const { Title, Text } = Typography;

const CollapsibleSampleData = ({ columnIndex, getSampleData, getTotalNonEmptyRows }) => {
  const lang = useSelector(getLang('SETTINGS'));
  const [isExpanded, setIsExpanded] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(3);
  const totalRows = getTotalNonEmptyRows(columnIndex);
  const sampleData = getSampleData(columnIndex, displayLimit);
  const hasMore = totalRows > displayLimit;
  const remainingRows = totalRows - displayLimit;
  const nextLoadCount = Math.min(5, remainingRows);

  const handleLoadMore = () => {
    setDisplayLimit(prev => Math.min(prev + 5, totalRows));
  };

  return (
    <div className={styles.sampleData}>
      <div className={styles.sampleDataHeader}>
        <Button
          type="text"
          onClick={() => setIsExpanded(!isExpanded)}
          icon={<FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />}
          className={styles.toggleButton}>
          {isExpanded ? lang.HIDE_SAMPLE_DATA : lang.SHOW_SAMPLE_DATA}
        </Button>
      </div>
      {isExpanded && (
        <>
          <Table
            dataSource={sampleData}
            columns={[
              {
                title: '#',
                dataIndex: 'index',
                key: 'index',
                width: 50,
              },
              {
                title: lang.VALUE,
                dataIndex: 'value',
                key: 'value',
              },
            ]}
            pagination={false}
            size="small"
          />
          {hasMore && (
            <Button type="link" onClick={handleLoadMore} className={styles.loadMoreButton}>
              Load {nextLoadCount} More ({remainingRows} remaining)
            </Button>
          )}
        </>
      )}
    </div>
  );
};

const ColumnMapper = ({ contextType }) => {
  const lang = useSelector(getLang('SETTINGS'));
  const {
    headers,
    fileData,
    propertyOptions,
    mapFileHeadersToUserProperties,
    mandatoryFields = [],
    columnMappings,
    setColumnMappings,
    headerAliases = {},
  } = useUploadContextFactory({ contextType });

  // Check if a column has any non-empty values
  const isColumnEmpty = columnIndex => {
    return fileData.every(row => {
      const value = row[columnIndex];
      return !value || value.trim() === '';
    });
  };

  const doInitialMapping = () => {
    // If we already have saved mappings, use them
    if (Object.keys(columnMappings).length > 0) {
      return columnMappings;
    }

    const mappedColumns = {};
    headers.forEach((header, index) => {
      // Skip empty columns (no header or all values are empty)
      if (!header || header.trim() === '' || isColumnEmpty(index)) {
        return;
      }

      const closestMatch = suggestProperty(header, Object.keys(propertyOptions), headerAliases);
      mappedColumns[header] = {
        value: closestMatch,
        isCollapsed: false,
        isMandatory: mandatoryFields.includes(closestMatch),
      };
    });
    return mappedColumns;
  };

  const [columnMapper, setColumnMap] = useState(doInitialMapping);

  const updateColumnMapState = (column, updates) => {
    setColumnMap(prev => {
      const updated = {
        ...prev,
        [column]: {
          value: updates.value ?? prev[column]?.value,
          isCollapsed: updates.isCollapsed ?? prev[column]?.isCollapsed ?? false,
          isMandatory: updates.isMandatory ?? prev[column]?.isMandatory ?? false,
        },
      };
      setColumnMappings(updated);
      return updated;
    });
  };

  const updateMapping = (column, value) => {
    updateColumnMapState(column, {
      value,
      isCollapsed: false,
      isMandatory: mandatoryFields.includes(value),
    });
  };

  const expandMapper = column => {
    updateColumnMapState(column, { isCollapsed: false });
  };

  const confirmMappingAndCollapse = column => {
    updateColumnMapState(column, { isCollapsed: true });
  };

  const removeMappingAndCollapse = column => {
    updateColumnMapState(column, {
      value: undefined,
      isCollapsed: true,
      isMandatory: false,
    });
  };

  const createColumnMappings = () => {
    return Object.entries(columnMapper).reduce((acc, [key, { value }]) => {
      if (value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});
  };

  const hasUnconfirmedColumns = Object.values(columnMapper).some(({ isCollapsed }) => !isCollapsed);
  const unmappedRequiredLabels = getUnmappedRequiredFields(columnMapper, mandatoryFields).map(
    field => propertyOptions[field],
  );
  const requiredLabels = mandatoryFields.map(field => propertyOptions[field]);
  const isValidateDisabled = hasUnconfirmedColumns || unmappedRequiredLabels.length > 0;

  const getSampleData = (columnIndex, limit) => {
    // Find rows that have non-empty values in this column
    const rowsWithValues = [];

    for (let i = 0; i < fileData.length && rowsWithValues.length < limit; i++) {
      const value = fileData[i][columnIndex];
      if (value && value.trim() !== '') {
        rowsWithValues.push({
          key: i,
          index: i + 1,
          value: value,
        });
      }
    }

    return rowsWithValues;
  };

  const getTotalNonEmptyRows = columnIndex => {
    let count = 0;
    for (let i = 0; i < fileData.length; i++) {
      const value = fileData[i][columnIndex];
      if (value && value.trim() !== '') {
        count++;
      }
    }
    return count;
  };

  return (
    <div className={styles.container}>
      <Title className={styles.title} level={3}>
        {lang.MAP_CSV_COLUMNS_TITLE}
      </Title>
      {requiredLabels.length > 0 && (
        <Text className={styles.requiredFields}>
          {lang.REQUIRED_FIELDS_NOTICE.replace('{fields}', requiredLabels.join(', '))}
        </Text>
      )}
      {fileData && (
        <div className={styles.mappingContainer}>
          {headers.map((col, index) => {
            // Skip columns that were filtered out
            if (!columnMapper[col]) {
              return null;
            }

            return (
              <Card
                key={col}
                role="group"
                aria-label={col}
                className={classNames(styles.columnCard, {
                  [styles.confirmed]: columnMapper[col].isCollapsed && columnMapper[col].value,
                  [styles.removed]: columnMapper[col].isCollapsed && !columnMapper[col].value,
                })}>
                {columnMapper[col].isCollapsed ? (
                  <div className={styles.collapsedMapper}>
                    <Text strong>{col}</Text>
                    {columnMapper[col].value && (
                      <Text type="secondary"> → {propertyOptions[columnMapper[col].value]}</Text>
                    )}
                    <Button onClick={() => expandMapper(col)}>{lang.EDIT}</Button>
                  </div>
                ) : (
                  <div className={styles.expandedMapper}>
                    <div className={styles.topRow}>
                      <div className={styles.columnHeader}>
                        <Text strong>
                          {lang.COLUMN}: {col}
                        </Text>
                        {columnMapper[col].isMandatory && (
                          <Text type="danger" className={styles.requiredLabel}>
                            {lang.REQUIRED}
                          </Text>
                        )}
                      </div>
                      <div className={styles.selectWrapper}>
                        <Select
                          value={columnMapper[col].value}
                          onChange={(name, value) => updateMapping(col, value)}
                          name={col}
                          placeholder={lang.SELECT_PROPERTY}
                          options={Object.entries(propertyOptions).map(
                            ([propertyKey, propertyValue]) => ({
                              label: mandatoryFields.includes(propertyKey)
                                ? `${propertyValue} ${lang.REQUIRED}`
                                : propertyValue,
                              value: propertyKey,
                            }),
                          )}
                          noMarginBottom
                        />
                      </div>
                      <div className={styles.actionButtons}>
                        <Button
                          type="primary"
                          onClick={() => confirmMappingAndCollapse(col)}
                          disabled={!columnMapper[col].value}>
                          {lang.CONFIRM}
                        </Button>
                        <Button
                          danger
                          onClick={() => removeMappingAndCollapse(col)}
                          disabled={columnMapper[col].isMandatory}>
                          {lang.REMOVE_COLUMN}
                        </Button>
                      </div>
                    </div>
                    <CollapsibleSampleData
                      columnIndex={index}
                      getSampleData={getSampleData}
                      getTotalNonEmptyRows={getTotalNonEmptyRows}
                    />
                  </div>
                )}
              </Card>
            );
          })}
          <div className={styles.footer}>
            <Button
              type="primary"
              size="large"
              onClick={() => mapFileHeadersToUserProperties(createColumnMappings())}
              disabled={isValidateDisabled}>
              {lang.PROCEED_TO_VALIDATION}
            </Button>
            {unmappedRequiredLabels.length > 0 && (
              <Alert
                message={lang.MAP_REQUIRED_FIELD.replace(
                  '{field}',
                  unmappedRequiredLabels.join(', '),
                )}
                type="error"
                showIcon
                className={styles.footerAlert}
              />
            )}
            {hasUnconfirmedColumns && (
              <Alert
                message={lang.COLUMN_MAPPING_WARNING}
                type="warning"
                showIcon
                className={styles.footerAlert}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnMapper;
